<template>
  <NavBar pageName="UsersView"></NavBar>
  <v-container v-if="computedShowUsersList" style="margin-top: 185px;">
    <!-- Title and Add New User Button -->
    <div class="page-header d-flex justify-space-between align-center mb-4">
      <div class="d-flex align-center">
        <h1>{{ t.users.title }}</h1>
        <v-tooltip text="Rebuild client hierarchy cache" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" icon="mdi-database-refresh" variant="text" size="small" class="ml-2" :loading="cacheRebuilding" @click="rebuildCache" />
          </template>
        </v-tooltip>
      </div>
      <v-btn color="primary" @click="openAddNewUser">{{ t.users.add_new_user }}</v-btn>
    </div>

    <!-- Search Section -->
    <v-card class="pa-4 mb-4">
      <v-row align="center">
        <!-- Client selector (autocomplete with tree) - only for users who can see subclients -->
        <v-col v-if="canSeeSubclients" cols="12" md="3">
          <v-autocomplete
              v-model="selectedClientId"
              :label="t.clients?.filter_by_parent || 'Select Client'"
              :items="parentClientsForSelect"
              :loading="loadingParentClients"
              :placeholder="t.common?.all || 'All'"
              clearable
              dense
              outlined
              hide-details
              auto-select-first
          ></v-autocomplete>
        </v-col>

        <!-- Radio buttons for Own / Include subclients - only for users who can see subclients -->
        <v-col v-if="canSeeSubclients" cols="12" md="3">
          <v-radio-group v-model="includeSubclients" inline hide-details density="compact">
            <v-radio :label="t.clients?.include_subclients || 'Include subclients'" :value="true"></v-radio>
            <v-radio :label="t.users?.own || 'Own'" :value="false"></v-radio>
          </v-radio-group>
        </v-col>

        <!-- Status filter radio buttons -->
        <v-col cols="12" md="4">
          <div class="text-caption mb-1">{{ t.users.status_filter }}</div>
          <v-radio-group v-model="statusFilter" inline hide-details density="compact">
            <v-radio :label="t.users.all_except_deleted" value="all_except_deleted"></v-radio>
            <v-radio :label="t.common.active" value="active"></v-radio>
            <v-radio :label="t.common.suspended" value="suspended"></v-radio>
            <v-radio :label="t.common.deleted" value="deleted"></v-radio>
          </v-radio-group>
        </v-col>

        <!-- Search button -->
        <v-col cols="12" md="2">
          <v-btn color="primary" @click="loadUsers" :loading="loading">
            {{ t.common.search }}
          </v-btn>
          <v-btn variant="outlined" class="ml-2" @click="resetSearch">
            {{ t.common.reset }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Filter displayed results -->
    <v-text-field
        v-model="searchUserQuery"
        :label="t.users.filter_results"
        :placeholder="t.users.filter_placeholder"
        clearable
        class="mb-4"
        prepend-inner-icon="mdi-magnify"
    ></v-text-field>

    <!-- Results count -->
    <div class="mb-2 text-caption" v-if="usersLoaded">
      {{ t.common.showing }} {{ filteredUsers.length }} {{ t.common.of }} {{ users.length }} {{ t.menu.users.toLowerCase() }}
    </div>

    <!-- Users Table -->
    <v-card>
      <v-table density="compact" hover>
        <thead>
          <tr class="bg-grey-lighten-3">
            <th>{{ t.common.name }}</th>
            <th>{{ t.common.username }}</th>
            <th>{{ t.common.email }}</th>
            <th>{{ t.common.phone }}</th>
            <th>{{ t.users.client }}</th>
            <th>{{ t.common.status }}</th>
            <th class="text-center">{{ t.common.actions }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in filteredUsers" :key="user.id" class="user-row">
            <td class="font-weight-medium">{{ user.name }}</td>
            <td>@{{ user.username }}</td>
            <td>{{ user.email || '-' }}</td>
            <td>{{ user.phone || '-' }}</td>
            <td>
              <a
                v-if="user.client_id"
                href="#"
                class="text-decoration-none"
                @click.prevent="goToClient(user.client_id)"
              >{{ user.client_name || '-' }}</a>
              <span v-else>{{ user.client_name || '-' }}</span>
            </td>
            <td>
              <v-chip :color="getStatusColor(user.user_status_name)" size="x-small">
                {{ user.user_status_name }}
              </v-chip>
            </td>
            <td class="text-center">
              <v-btn icon variant="text" size="small" color="primary" @click="openUserDetails(user)">
                <v-icon size="20">mdi-eye</v-icon>
              </v-btn>
              <v-btn icon variant="text" size="small" color="info" @click="openEditDialog(user)">
                <v-icon size="20">mdi-pencil</v-icon>
              </v-btn>
              <v-btn v-if="canManageRoles" icon variant="text" size="small" color="warning" @click="openRolesMenu(user)">
                <v-icon size="20">mdi-shield-account</v-icon>
              </v-btn>
              <v-btn icon variant="text" size="small" :color="user.user_status_name === 'suspended' ? 'success' : 'error'" @click="confirmSuspendUser(user)" :disabled="!canManageUsers">
                <v-icon size="20">{{ user.user_status_name === 'suspended' ? 'mdi-account-check' : 'mdi-account-off' }}</v-icon>
              </v-btn>
              <v-btn icon variant="text" size="small" color="error" @click="confirmDeleteUser(user)" :disabled="!canManageUsers || user.user_status_name === 'deleted'">
                <v-icon size="20">mdi-delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <!-- User Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px">
      <v-card v-if="selectedUserDetails">
        <v-card-title class="bg-primary text-white d-flex justify-space-between align-center">
          <span>{{ selectedUserDetails.name }}</span>
          <v-chip :color="getStatusColor(selectedUserDetails.user_status_name)" size="small">
            {{ selectedUserDetails.user_status_name }}
          </v-chip>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-table density="compact">
            <tbody>
              <tr>
                <td class="font-weight-medium" width="40%">ID</td>
                <td>{{ selectedUserDetails.id }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.common.username }}</td>
                <td>@{{ selectedUserDetails.username }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.common.email }}</td>
                <td>{{ selectedUserDetails.email || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.common.phone }}</td>
                <td>{{ selectedUserDetails.phone || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.client }}</td>
                <td>{{ selectedUserDetails.client_name || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.app }}</td>
                <td>{{ selectedUserDetails.app_name || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.parent_user }}</td>
                <td>{{ selectedUserDetails.parent_username || selectedUserDetails.parent_email || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.distributor_id }}</td>
                <td>{{ selectedUserDetails.distributor_id || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.email_confirmed }}</td>
                <td>
                  <v-icon :color="selectedUserDetails.email_confirmed ? 'success' : 'error'" size="small">
                    {{ selectedUserDetails.email_confirmed ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.phone_confirmed }}</td>
                <td>
                  <v-icon :color="selectedUserDetails.phone_confirmed ? 'success' : 'error'" size="small">
                    {{ selectedUserDetails.phone_confirmed ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                </td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.failed_logins }}</td>
                <td>{{ selectedUserDetails.failed_logins || 0 }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.users.two_factor }}</td>
                <td>{{ selectedUserDetails.twofa ? t.users.enabled : t.users.disabled }}</td>
              </tr>
              <tr v-if="selectedUserDetails.description">
                <td class="font-weight-medium">{{ t.common.description }}</td>
                <td>{{ selectedUserDetails.description }}</td>
              </tr>
              <tr v-if="selectedUserDetails.comments">
                <td class="font-weight-medium">{{ t.users.comments }}</td>
                <td>{{ selectedUserDetails.comments }}</td>
              </tr>
              <tr v-if="canViewConfirmationCode">
                <td class="font-weight-medium">{{ t.users.confirmation_code }}</td>
                <td>{{ selectedUserDetails.confirmation_code || '-' }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.common.created_at }}</td>
                <td>{{ formatDate(selectedUserDetails.created_at) }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">{{ t.common.updated_at }}</td>
                <td>{{ formatDate(selectedUserDetails.updated_at) }}</td>
              </tr>
            </tbody>
          </v-table>

          <!-- Auth & Notification Status (read-only) -->
          <v-divider class="my-3"></v-divider>
          <div class="text-subtitle-2 mb-2">{{ t.users.authentication_notifications }}</div>
          <div class="d-flex ga-4 flex-wrap">
            <v-chip :color="selectedUserDetails.auth_email ? 'success' : 'grey'" size="small" variant="tonal">
              <v-icon size="small" class="mr-1">{{ selectedUserDetails.auth_email ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
              {{ t.users.auth_email_label }}
            </v-chip>
            <v-chip :color="selectedUserDetails.auth_sms ? 'success' : 'grey'" size="small" variant="tonal">
              <v-icon size="small" class="mr-1">{{ selectedUserDetails.auth_sms ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
              {{ t.users.auth_sms_label }}
            </v-chip>
            <v-chip :color="selectedUserDetails.email_notification ? 'success' : 'grey'" size="small" variant="tonal">
              <v-icon size="small" class="mr-1">{{ selectedUserDetails.email_notification ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
              {{ t.users.email_notifications }}
            </v-chip>
            <v-chip :color="selectedUserDetails.sms_notification ? 'success' : 'grey'" size="small" variant="tonal">
              <v-icon size="small" class="mr-1">{{ selectedUserDetails.sms_notification ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
              {{ t.users.sms_notifications }}
            </v-chip>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn :color="selectedUserDetails.user_status_name === 'suspended' ? 'success' : 'error'" variant="elevated" size="small" @click="confirmSuspendUser(selectedUserDetails)" :disabled="!canManageUsers">
            <v-icon size="20" class="mr-1">{{ selectedUserDetails.user_status_name === 'suspended' ? 'mdi-account-check' : 'mdi-account-off' }}</v-icon>
            {{ selectedUserDetails.user_status_name === 'suspended' ? (t.common.activate || 'Activate') : (t.common.suspend || 'Suspend') }}
          </v-btn>
          <v-btn color="error" variant="elevated" size="small" @click="confirmDeleteUser(selectedUserDetails)" :disabled="!canManageUsers || selectedUserDetails.user_status_name === 'deleted'">
            <v-icon size="20" class="mr-1">mdi-delete</v-icon>
            {{ t.common.delete }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="elevated" size="small" @click="openEditDialog(selectedUserDetails)">{{ t.common.edit }}</v-btn>
          <v-btn v-if="canManageRoles" color="info" variant="elevated" size="small" @click="openRolesMenu(selectedUserDetails)">{{ t.users.roles }}</v-btn>
          <v-btn color="grey" variant="text" @click="detailsDialog = false">{{ t.common.close }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>

  <!-- Add New User Component -->
  <AddNewUser @showAddNewUserEvent="setShowAddNewUser" @cancelAddNewUserEvent="setShowUsersList" v-if="showComputedFinalAddNewUser"></AddNewUser>

  <!-- Edit User Dialog -->
  <v-dialog v-model="editDialog" max-width="500px">
    <v-card>
      <v-card-title class="bg-primary text-white">
        {{ t.users.edit_user }}
      </v-card-title>
      <v-card-text class="pt-4">
        <v-text-field
            v-model="editForm.name"
            :label="t.common.name"
            outlined
            dense
        ></v-text-field>
        <v-text-field
            v-model="editForm.username"
            :label="t.common.username"
            outlined
            dense
        ></v-text-field>
        <v-text-field
            v-model="editForm.email"
            :label="t.common.email"
            outlined
            dense
            type="email"
        ></v-text-field>
        <v-text-field
            v-model="editForm.phone"
            :label="t.common.phone"
            outlined
            dense
        ></v-text-field>
        <v-text-field
            v-model="editForm.password"
            :label="t.users.new_password_hint"
            outlined
            dense
            type="password"
            :hint="t.common.min_length + ' 3 ' + t.common.characters"
        ></v-text-field>

        <v-divider class="my-3"></v-divider>
        <div class="text-subtitle-2 mb-2">{{ t.users.authentication }}</div>
        <v-row dense>
          <v-col cols="6">
            <v-switch
                v-model="editForm.auth_email"
                :label="t.users.auth_email_label"
                color="success"
                hide-details
                density="compact"
            ></v-switch>
          </v-col>
          <v-col cols="6">
            <v-switch
                v-model="editForm.auth_sms"
                :label="t.users.auth_sms_label"
                color="success"
                hide-details
                density="compact"
            ></v-switch>
          </v-col>
        </v-row>
        <div class="text-subtitle-2 mb-2 mt-3">{{ t.users.notifications }}</div>
        <v-row dense>
          <v-col cols="6">
            <v-switch
                v-model="editForm.email_notification"
                :label="t.users.email_notifications"
                color="success"
                hide-details
                density="compact"
            ></v-switch>
          </v-col>
          <v-col cols="6">
            <v-switch
                v-model="editForm.sms_notification"
                :label="t.users.sms_notifications"
                color="success"
                hide-details
                density="compact"
            ></v-switch>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" variant="text" @click="editDialog = false">{{ t.common.cancel }}</v-btn>
        <v-btn color="primary" variant="elevated" @click="saveUserProfile" :loading="editLoading">{{ t.common.save }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Roles Menu -->
  <v-menu v-model="rolesMenuOpen" :close-on-content-click="false">
    <div style="position:fixed;top:70px;left:80%;z-index: 5;">
      <v-btn color="error" variant="elevated" elevation="12" @click="closeRolesMenu">{{ t.common.close }}</v-btn>
    </div>
    <v-card class="border1" style="width:650px;margin-top:60px;">
      <v-card-title>{{ t.users.roles_for }} {{ selectedUserForRoles?.name }}</v-card-title>
      <v-card-text>
        <v-list>
          <v-list-item v-for="role in userRoles" :key="role.role_id">
            <v-row class="flex-row align-content-center justify-start">
              <v-col>
                <div style="font-size: 18px;width:360px;" :class="role.active==true?'roleActive':'roleInactive'">
                  {{ role.name.toUpperCase() }}
                </div>
              </v-col>
              <v-col>
                <v-btn
                    v-if="role.can_toggle !== false"
                    style="width: 150px;"
                    :color="role.active === true ? 'error' : 'success'"
                    @click="toggleRole(role.id)"
                    :loading="role_loading"
                >
                  {{ role.active === true ? t.users.deactivate : t.users.activate }}
                </v-btn>
              </v-col>
            </v-row>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="closeRolesMenu">{{ t.common.close }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>

  <!-- Suspend/Activate User Confirmation Dialog -->
  <v-dialog v-model="suspendDialog" max-width="400">
    <v-card v-if="userToSuspend">
      <v-card-title :class="userToSuspend.user_status_name === 'suspended' ? 'bg-success text-white' : 'bg-error text-white'">
        <v-icon class="mr-2">{{ userToSuspend.user_status_name === 'suspended' ? 'mdi-account-check' : 'mdi-account-off' }}</v-icon>
        {{ userToSuspend.user_status_name === 'suspended' ? (t.common.activate || 'Activate') : (t.common.suspend || 'Suspend') }} {{ t.menu.users.toLowerCase() }}
      </v-card-title>
      <v-card-text class="pt-4">
        <p>{{ t.messages.confirm_action }}</p>
        <p class="font-weight-bold mt-2">{{ userToSuspend.name }} (@{{ userToSuspend.username }})</p>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" variant="text" @click="suspendDialog = false">{{ t.common.cancel }}</v-btn>
        <v-btn :color="userToSuspend.user_status_name === 'suspended' ? 'success' : 'error'" variant="elevated" @click="toggleSuspendUser" :loading="actionLoading">
          {{ userToSuspend.user_status_name === 'suspended' ? (t.common.activate || 'Activate') : (t.common.suspend || 'Suspend') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete User Confirmation Dialog -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title class="bg-error text-white">
        <v-icon class="mr-2">mdi-delete</v-icon>
        {{ t.common.delete }} {{ t.menu.users.toLowerCase() }}
      </v-card-title>
      <v-card-text class="pt-4">
        <p>{{ t.messages.confirm_action }}</p>
        <p class="font-weight-bold mt-2" v-if="userToDelete">{{ userToDelete.name }} (@{{ userToDelete.username }})</p>
        <v-alert type="warning" density="compact" class="mt-3">
          {{ t.messages.action_irreversible || 'This action cannot be undone.' }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" variant="text" @click="deleteDialog = false">{{ t.common.cancel }}</v-btn>
        <v-btn color="error" variant="elevated" @click="deleteUser" :loading="actionLoading">{{ t.common.delete }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import {log} from "@/composables/useLog";
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import axios from 'axios'
import {BASE_URL} from "@/composables/url_config";
import useGetClientsAPI from "@/composables/useGetClientsAPI";
import {getClientTree} from "@/composables/useGetClientsAPI";
import AddNewUser from "@/components/AddNewUser.vue";
import useGetUserRoles from "@/composables/useGetUserRoles";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'UsersView',
  components: {
    AddNewUser,
    NavBar
  },
  data() {
    return {
      showToken: false,
      showUsersList: true,
      showAddNewUserComp: false,
      token: '',
      user: null,
      usersListLoaded: true,
      usersLoaded: false,
      users: [],
      searchUserQuery: "",
      clientsLoadedForNewUser: true,
      canViewConfirmationCode: false,
      loading: false,

      // Search controls
      selectedClientId: null,
      includeSubclients: false,
      parentClientsList: [],
      loadingParentClients: false,
      statusFilter: 'all_except_deleted',
      canSeeSubclients: false,

      // Details dialog
      detailsDialog: false,
      selectedUserDetails: null,

      // Edit dialog
      editDialog: false,
      editLoading: false,
      editForm: {
        user_id: null,
        name: '',
        username: '',
        email: '',
        phone: '',
        password: '',
        auth_sms: false,
        auth_email: false,
        email_notification: false,
        sms_notification: false
      },

      // Roles
      rolesMenuOpen: false,
      role_loading: false,
      selectedUserForRoles: null,
      userRoles: [],

      // Suspend/Delete
      suspendDialog: false,
      deleteDialog: false,
      userToSuspend: null,
      userToDelete: null,
      actionLoading: false,
      cacheRebuilding: false
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    filteredUsers() {
      const query = this.searchUserQuery?.toLowerCase() || '';
      return this.users.filter((user) => {
        return (
            user.name?.toLowerCase().includes(query) ||
            user.username?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query) ||
            user.phone?.toLowerCase().includes(query)
        );
      });
    },
    showComputedFinalAddNewUser() {
      return this.showAddNewUserComp && this.clientsLoadedForNewUser;
    },
    computedShowUsersList() {
      return this.usersListLoaded && this.showUsersList;
    },
    parentClientsForSelect() {
      return this.parentClientsList.map(item => ({
        value: item.id,
        title: item.displayName
      }));
    },
    canManageUsers() {
      if (!this.user) return false;
      if (this.user.is_admin || this.user.admin) return true;

      // Get role names from user object
      let roleNames = [];
      if (this.user.roles && Array.isArray(this.user.roles)) {
        roleNames = this.user.roles.map(r => {
          if (typeof r === 'string') return r.toLowerCase();
          if (r && r.name) return r.name.toLowerCase();
          if (r && r.role_name) return r.role_name.toLowerCase();
          return '';
        });
      } else if (this.user.role) {
        roleNames = [this.user.role.toLowerCase()];
      } else if (this.user.role_name) {
        roleNames = [this.user.role_name.toLowerCase()];
      }

      log("canManageUsers - user.roles:", this.user.roles);
      log("canManageUsers - roleNames:", roleNames);

      const allowedRoles = ['root', 'admin', 'users_management', 'default_admin_l0'];
      const result = roleNames.some(role => allowedRoles.includes(role));
      log("canManageUsers - result:", result);
      return result;
    },
    canManageRoles() {
      if (!this.user) return false;
      let roleNames = [];
      if (this.user.roles && Array.isArray(this.user.roles)) {
        roleNames = this.user.roles.map(r => {
          if (typeof r === 'string') return r.toLowerCase();
          if (r && r.name) return r.name.toLowerCase();
          if (r && r.role_name) return r.role_name.toLowerCase();
          return '';
        });
      }
      return roleNames.some(role => ['root', 'change_roles'].includes(role));
    }
  },
  watch: {
    selectedClientId() {
      // Clear users list when switching client so user knows to click Search
      this.users = [];
      this.usersLoaded = false;
    },
    includeSubclients() {
      this.users = [];
      this.usersLoaded = false;
    }
  },
  methods: {
    setShowUsersList() {
      this.showAddNewUserComp = false;
      this.showUsersList = true;
      this.loadUsers();
    },
    openAddNewUser() {
      log("facem showAddNewUserComp=true");
      this.showAddNewUserComp = true;
      this.showUsersList = false;
    },
    setShowAddNewUser() {
      log("chemam setShowAddNewUser");
      this.clientsLoadedForNewUser = true;
      this.showUsersList = false;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString();
    },
    getStatusColor(statusName) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'terminated': 'error',
        'pending': 'info'
      };
      return colors[statusName?.toLowerCase()] || 'grey';
    },

    // View user details
    openUserDetails(user) {
      this.selectedUserDetails = user;
      this.detailsDialog = true;
    },

    // Edit user
    openEditDialog(user) {
      this.editForm = {
        user_id: user.id,
        name: user.name || '',
        username: user.username || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        auth_sms: user.auth_sms || false,
        auth_email: user.auth_email || false,
        email_notification: user.email_notification || false,
        sms_notification: user.sms_notification || false
      };
      this.editDialog = true;
    },
    async saveUserProfile() {
      this.editLoading = true;
      try {
        const headers = {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json"
        };
        const response = await axios.post(`${BASE_URL}/home/update_user_profile_api`, this.editForm, { headers });

        if (response.data.error) {
          alert(response.data.message || 'Error updating user');
        } else {
          alert('User updated successfully');
          this.editDialog = false;
          this.loadUsers();
        }
      } catch (error) {
        console.error("Error updating user:", error);
        alert("Error updating user");
      }
      this.editLoading = false;
    },

    // Toggle auth settings
    async toggleAuth(user, authField) {
      try {
        const headers = {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json"
        };
        const response = await axios.post(`${BASE_URL}/home/toggle_user_auth_api`, {
          user_id: user.id,
          auth_field: authField
        }, { headers });

        if (response.data.error) {
          alert(response.data.message || 'Error toggling auth');
          // Revert the toggle
          user[authField] = !user[authField];
        }
      } catch (error) {
        console.error("Error toggling auth:", error);
        // Revert the toggle
        user[authField] = !user[authField];
      }
    },

    // Build flat list from client tree with indentation
    buildParentClientsList(clientsTree) {
      const list = [];
      const addClientsRecursively = (clients, level = 0) => {
        if (!clients || clients.length === 0) return;
        clients.forEach(client => {
          const indent = level > 0 ? '—'.repeat(level) + ' ' : '';
          list.push({
            id: client.client_id || client.id,
            name: client.name,
            displayName: indent + client.name,
            level: level
          });
          if (client.children && client.children.length > 0) {
            addClientsRecursively(client.children, level + 1);
          }
        });
      };
      addClientsRecursively(clientsTree);
      return list;
    },

    // Load parent clients for autocomplete
    async fetchParentClients() {
      try {
        this.loadingParentClients = true;
        const response = await getClientTree(this.token, this.user.client_id);
        if (!response.error && response.clients_list) {
          this.parentClientsList = this.buildParentClientsList(response.clients_list);
        }
      } catch (error) {
        console.error("Error loading parent clients:", error);
      } finally {
        this.loadingParentClients = false;
      }
    },

    // Load users based on search mode
    loadUsers() {
      this.loading = true;
      const { getClientUsers } = useGetClientsAPI();

      // Use selected client or default to user's client
      let clientId = this.selectedClientId || this.user.client_id;
      let options = {
        status_filter: this.statusFilter,
        include_subclients: this.includeSubclients
      };

      getClientUsers(this.token, clientId, options).then((result) => {
        this.users = result.users || [];
        this.canViewConfirmationCode = result.can_view_confirmation_code || false;
        if (result.can_see_subclients !== undefined) {
          this.canSeeSubclients = result.can_see_subclients;
          // Load parent clients tree if user can see subclients and not yet loaded
          if (this.canSeeSubclients && this.parentClientsList.length === 0) {
            this.fetchParentClients();
          }
        }
        this.usersLoaded = true;
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },

    goToClient(clientId) {
      this.$router.push({ path: '/clients', query: { client_id: clientId } });
    },

    resetSearch() {
      this.selectedClientId = null;
      this.includeSubclients = false;
      this.searchUserQuery = '';
      this.statusFilter = 'all_except_deleted';
      this.loadUsers();
    },

    // Roles functionality
    openRolesMenu(user) {
      this.selectedUserForRoles = user;
      this.rolesMenuOpen = true;
      this.callGetUserRoles();
    },
    closeRolesMenu() {
      this.rolesMenuOpen = false;
    },
    async callGetUserRoles() {
      const { getUserRoles } = useGetUserRoles();
      getUserRoles(this.token, this.selectedUserForRoles.id).then((response) => {
        this.userRoles = response.user_roles || [];
      });
    },
    async toggleRole(role_id) {
      try {
        this.role_loading = true;
        const headers = {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json",
        };
        const params = { role_id: role_id, user_id: this.selectedUserForRoles.id };
        const url = BASE_URL + '/home/toggleUserRole';
        log(params);
        const response = await axios.post(url, params, { headers });
        log("toggleRole");
        log(response);
        this.role_loading = false;
        this.userRoles = response.data.user_roles;
      } catch (error) {
        console.error("Error toggling role:", error);
        this.role_loading = false;
      }
    },

    // Suspend/Activate user
    confirmSuspendUser(user) {
      this.userToSuspend = user;
      this.suspendDialog = true;
    },
    async toggleSuspendUser() {
      if (!this.userToSuspend) return;
      this.actionLoading = true;

      // Toggle between suspended and active
      const newStatus = this.userToSuspend.user_status_name === 'suspended' ? 'active' : 'suspended';

      try {
        const headers = {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json"
        };
        const response = await axios.post(`${BASE_URL}/home/updateUserStatusApi`, {
          user_id: this.userToSuspend.id,
          status: newStatus
        }, { headers });

        if (response.data.error) {
          alert(response.data.message || 'Error updating user status');
        } else {
          this.suspendDialog = false;
          this.detailsDialog = false;
          this.loadUsers();
        }
      } catch (error) {
        console.error("Error updating user status:", error);
        alert("Error updating user status");
      }
      this.actionLoading = false;
    },

    // Rebuild hierarchy cache
    async rebuildCache() {
      this.cacheRebuilding = true
      try {
        const { rebuildHierarchyCache } = await import('@/composables/useHierarchyCacheAPI')
        const result = await rebuildHierarchyCache(this.token)
        if (!result.error) {
          alert('Cache rebuild started successfully')
        } else {
          alert(result.message || 'Error rebuilding cache')
        }
      } catch (e) {
        alert('Error rebuilding cache')
      } finally {
        this.cacheRebuilding = false
      }
    },

    // Delete user
    confirmDeleteUser(user) {
      this.userToDelete = user;
      this.deleteDialog = true;
    },
    async deleteUser() {
      if (!this.userToDelete) return;
      this.actionLoading = true;
      try {
        const headers = {
          Authorization: `Bearer ${this.token}`,
          "Content-Type": "application/json"
        };
        const response = await axios.post(`${BASE_URL}/home/updateUserStatusApi`, {
          user_id: this.userToDelete.id,
          status: 'deleted'
        }, { headers });

        if (response.data.error) {
          alert(response.data.message || 'Error deleting user');
        } else {
          this.deleteDialog = false;
          this.detailsDialog = false;
          this.loadUsers();
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        alert("Error deleting user");
      }
      this.actionLoading = false;
    }
  },
  created() {
    const { getLoggedIn, user, loggedIn, token } = useGetLoggedIn();

    getLoggedIn().then(() => {
      log("dupa created in UsersView avem:");
      log("user:", user);
      log("loggedIn: " + loggedIn.value);
      log("token: " + token.value);
      this.token = token.value;
      this.user = user.value;
      store.commit('setCurrentView', this.currentView);

      // Check for search query parameter
      if (this.$route.query.search) {
        this.searchUserQuery = this.$route.query.search;
      }

      // Check for client_id query parameter (from Clients page)
      if (this.$route.query.client_id) {
        this.selectedClientId = parseInt(this.$route.query.client_id);
      }

      // Load users initially with default filters (all except deleted)
      this.loadUsers();
    });
  }
});
</script>

<style scoped>
.page-header {
  margin-bottom: 20px;
}
.roleActive {
  color: #2e7d32;
  background-color: #e8f5e9;
  font-weight: bold;
  padding: 8px 16px;
  border-radius: 4px;
}
.roleInactive {
  color: #616161;
  background-color: #f5f5f5;
  padding: 8px 16px;
  border-radius: 4px;
}
.user-row:hover {
  background-color: #f5f5f5;
}
</style>
